import { useEffect, useMemo, useState } from "react";

import { useNotification, useAuth } from "../../../hooks";
import { useCart, useSupplier } from "../../../hooks/query";
import type { LaborLineEdit } from "../types";
import type { LaborRowItem } from "../types";
import { buildLaborSupplierComparisonValues } from "../utils/buildLaborSupplierComparisonValues";
import type { SupplierComparisonCartItem } from "../utils/buildSupplierComparisonValues";
import {
    cloneLaborEdit,
    hasLaborChanges,
    normalizeUnitPrice,
} from "../utils/cartMapping";
import useEstimateContractContextManager from "./useEstimateContractContextManager";

type UseLaborSupplierComparisonOptions = {
    projectId?: string;
    laborRows: LaborRowItem[];
};

function buildItemSupplierSelections(
    selectedValues: Record<string, SupplierComparisonCartItem>,
) {
    const mapping: Record<string, string> = {};

    for (const item of Object.values(selectedValues)) {
        if (!item?.supplierName) continue;
        const key = item.manufacturerProductId || item.value;
        if (key) {
            mapping[key] = item.supplierName;
        }
    }

    return mapping;
}

function resolveSupplierRecord(
    suppliers: Array<{ Id?: string; id?: string; Name?: string }>,
    supplierName: string,
) {
    return suppliers.find((entry) => entry.Name === supplierName);
}

function resolveSupplierId(
    suppliers: Array<{ Id?: string; id?: string; Name?: string }>,
    supplierName: string,
): string | null {
    const supplier = resolveSupplierRecord(suppliers, supplierName);
    const supplierId = supplier?.Id ?? supplier?.id ?? null;
    return supplierId && String(supplierId).trim() ? String(supplierId) : null;
}

function resolveComparisonUnitPrice(
    comparisonResults: any[],
    supplierName: string,
    item: SupplierComparisonCartItem,
) {
    const itemKey = item.manufacturerProductId || item.value;
    if (!itemKey) return null;

    const comparisonSupplierData = comparisonResults.find(
        (supplier) => supplier.SupplierName === supplierName,
    );
    if (!comparisonSupplierData?.SupplierInventory) {
        return null;
    }

    const itemData = comparisonSupplierData.SupplierInventory.find(
        (inventory: any) => {
            const inventoryKey =
                inventory.LaborServiceId ||
                inventory.ManufacturerProductId ||
                inventory.ProductName;
            return inventoryKey === itemKey;
        },
    );

    if (itemData?.UnitPrice === null || itemData?.UnitPrice === undefined) {
        return null;
    }

    const price = Number(itemData.UnitPrice);
    return Number.isFinite(price) && price > 0 ? price : null;
}

export function useLaborSupplierComparison({
    projectId,
    laborRows,
}: UseLaborSupplierComparisonOptions) {
    const { values, referenceValue, patchLaborEdits, setReferenceValue } =
        useEstimateContractContextManager();
    const { showSuccess, showError } = useNotification();
    const { selectedOrganization } = useAuth();
    const { useSupplierComparison, useList } = useSupplier();
    const comparisonSvc = useSupplierComparison();
    const { mutateAsync: updateCartItem } = useCart().useUpdate();
    const { data: suppliers = [], isLoading: isLoadingSuppliers } = useList({
        OrganizationId: selectedOrganization?.Id,
        Type: "Labor",
    });

    const selectedValues = useMemo(
        () =>
            buildLaborSupplierComparisonValues(
                laborRows,
                values.labor,
                referenceValue.cartIds || {},
            ),
        [laborRows, values.labor, referenceValue.cartIds],
    );

    const comparableItemCount = Object.keys(selectedValues).length;
    const hasUnsavedLaborChanges = hasLaborChanges(
        values.labor,
        referenceValue.labor,
    );
    const canCompare =
        !!projectId && comparableItemCount > 0 && !hasUnsavedLaborChanges;

    const itemSupplierSelections = useMemo(
        () => buildItemSupplierSelections(selectedValues),
        [selectedValues],
    );

    const [hasInitializedSuppliers, setHasInitializedSuppliers] =
        useState(false);
    const [selectedSupplierNames, setSelectedSupplierNames] = useState<
        string[]
    >([]);
    const [openModal, setOpenModal] = useState(false);
    const [comparisonResults, setComparisonResults] = useState<any[]>([]);
    const [isComparing, setIsComparing] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [localSelections, setLocalSelections] = useState<
        Record<string, string>
    >({});

    useEffect(() => {
        const uniqueSuppliers = Array.from(
            new Set(Object.values(itemSupplierSelections)),
        );
        if (!hasInitializedSuppliers && uniqueSuppliers.length > 0) {
            setSelectedSupplierNames(uniqueSuppliers);
            setHasInitializedSuppliers(true);
        }
    }, [itemSupplierSelections, hasInitializedSuppliers]);

    useEffect(() => {
        if (openModal) {
            setLocalSelections(itemSupplierSelections);
        }
    }, [openModal, itemSupplierSelections]);

    async function handleCompare() {
        if (!projectId || selectedSupplierNames.length === 0) return;

        setOpenModal(true);
        setIsComparing(true);

        try {
            const results = await comparisonSvc.mutateAsync({
                SupplierNames: selectedSupplierNames,
                ProjectId: projectId,
                Category: "Labor",
            });
            setComparisonResults(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error(
                "[EstimateContract] labor supplier comparison failed",
                error,
            );
            showError("Failed to compare labor suppliers");
            setOpenModal(false);
        } finally {
            setIsComparing(false);
        }
    }

    function onItemSupplierSelect(itemKey: string, supplierName: string) {
        setLocalSelections((prev) => ({ ...prev, [itemKey]: supplierName }));
    }

    async function handleConfirm() {
        setIsConfirming(true);

        try {
            const laborPatches: Record<string, Partial<LaborLineEdit>> = {};

            await Promise.all(
                Object.entries(selectedValues).map(async ([editKey, item]) => {
                    const itemKey = item.manufacturerProductId || item.value;
                    const chosenSupplierName = localSelections[itemKey];
                    if (!chosenSupplierName) return;

                    const supplierId = resolveSupplierId(
                        suppliers,
                        chosenSupplierName,
                    );
                    if (!supplierId) return;

                    const supplier = resolveSupplierRecord(
                        suppliers,
                        chosenSupplierName,
                    );
                    if (!supplier) return;

                    const unitPrice = resolveComparisonUnitPrice(
                        comparisonResults,
                        chosenSupplierName,
                        item,
                    );

                    const payload: Record<string, unknown> = {
                        Id: item.id,
                        SupplierId: supplierId,
                    };
                    if (unitPrice !== null) {
                        payload.UnitPrice = unitPrice;
                    }

                    await updateCartItem(payload);

                    laborPatches[editKey] = {
                        supplierId,
                        supplierName: supplier.Name ?? chosenSupplierName,
                        supplierUnitPrice: normalizeUnitPrice(unitPrice),
                    };
                }),
            );

            if (Object.keys(laborPatches).length === 0) {
                showError("Select a supplier for at least one labor item");
                return;
            }

            patchLaborEdits(laborPatches);
            setReferenceValue((prev: any) => {
                const labor = { ...prev.labor };
                for (const [editKey, patch] of Object.entries(laborPatches)) {
                    labor[editKey] = cloneLaborEdit({
                        ...labor[editKey],
                        ...patch,
                    } as LaborLineEdit);
                }
                return { ...prev, labor };
            });

            setOpenModal(false);
            showSuccess("Labor supplier selections saved");
        } catch (error) {
            console.error(
                "[EstimateContract] failed to save labor supplier selections",
                error,
            );
            showError("Failed to save labor supplier selections");
        } finally {
            setIsConfirming(false);
        }
    }

    return {
        suppliers,
        selectedValues,
        comparableItemCount,
        canCompare,
        hasUnsavedLaborChanges,
        selectedSupplierNames,
        setSelectedSupplierNames,
        openModal,
        setOpenModal,
        comparisonResults,
        isComparing,
        isConfirming,
        isLoadingSuppliers,
        localSelections,
        itemSupplierSelections,
        handleCompare,
        onItemSupplierSelect,
        handleConfirm,
    };
}
